import React, { useState, useEffect } from 'react';
import { Gift, giftApi } from '../../services/giftApi';
import { PlusCircle, Edit2, Trash2, Eye, X, ShoppingBagIcon, Upload, Loader2, ImageIcon, AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '../../config/apiConfig';
import { useToast } from '../../contexts/ToastContext';
import { GiftCategoryManager } from '../../components/Admin/GiftCategoryManager';
import { settingsApi } from '../../services/settingsApi';

const GiftsAdmin: React.FC = () => {
    const [gifts, setGifts] = useState<Gift[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingGift, setEditingGift] = useState<Gift | null>(null);
    const [viewingGift, setViewingGift] = useState<Gift | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [customCategories, setCustomCategories] = useState<string[]>([]);
    const [cachedFormData, setCachedFormData] = useState<any>(null);
    const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [deletingGiftId, setDeletingGiftId] = useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const { showSuccess, showError, showWarning } = useToast();
    const API_BASE = API_BASE_URL.replace(/\/api$/, '');

    useEffect(() => {
        fetchGifts();
        fetchSettings();
        
        const cached = localStorage.getItem('gifts_form_cache');
        if (cached) {
            try { setCachedFormData(JSON.parse(cached)); } catch (e) {}
        }

        return () => {
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, []);

    const fetchSettings = async () => {
        try {
            const settings = await settingsApi.getSettings();
            if (settings.gift_category_order) {
                setCustomCategories(settings.gift_category_order);
            }
        } catch (e) {
            console.error('Failed to fetch settings', e);
        }
    };

    const fetchGifts = async () => {
        try {
            setLoading(true);
            const data = await giftApi.getAllGifts(false);
            setGifts(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Failed to fetch gifts');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const executeDelete = async () => {
        if (!deletingGiftId) return;
        try {
            await giftApi.deleteGift(deletingGiftId);
            setGifts(gifts.filter((g) => g.id !== deletingGiftId));
            showSuccess('Product deleted successfully', 'Inventory Management');
        } catch (err) {
            console.error('Failed to delete gift', err);
            showError('Failed to delete product', 'Inventory Error');
        } finally {
            setDeletingGiftId(null);
        }
    };

    const handleDelete = (id: string) => {
        setDeletingGiftId(id);
    };

    const handleSave = async (giftData: Partial<Gift>) => {
        if (isUploading) {
            showWarning('Please wait for the image to finish uploading', 'Upload in Progress');
            return;
        }
        try {
            setIsSaving(true);
            if (editingGift) {
                const response = await giftApi.updateGift(editingGift.id, giftData);
                if (response) {
                    await fetchGifts();
                }
            } else {
                const response = await giftApi.createGift(giftData);
                if (response) {
                    await fetchGifts();
                }
                localStorage.removeItem('gifts_form_cache');
                setCachedFormData(null);
            }
            closeModal();
            showSuccess(`Product ${editingGift ? 'updated' : 'created'} successfully`, 'Inventory Management');
        } catch (err) {
            console.error('Failed to save gift', err);
            showError('Failed to save product. Check console for details.', 'Inventory Error');
        } finally {
            setIsSaving(false);
        }
    };

    const openEditModal = (gift: Gift) => {
        setEditingGift(gift);
        setCurrentImageUrl(gift.imageUrl || '');
        setPreviewUrl('');
        setIsModalOpen(true);
    };

    const openAddModal = () => {
        setEditingGift(null);
        setCurrentImageUrl(cachedFormData?.imageUrl || '');
        setPreviewUrl('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl('');
        setIsModalOpen(false);
        setEditingGift(null);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Cleanup old preview
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        // Instant local preview
        const localUrl = URL.createObjectURL(file);
        setPreviewUrl(localUrl);

        try {
            setIsUploading(true);
            const response = await giftApi.uploadImage(file);
            if (response && response.imageUrl) {
                setCurrentImageUrl(response.imageUrl);
                console.log('Upload success, server URL:', response.imageUrl);
            }
        } catch (err) {
            console.error('Upload failed', err);
            showError('Failed to upload image. Upload took longer than expected.', 'Upload Timeout');
            setPreviewUrl(''); // Revert preview on failure
        } finally {
            setIsUploading(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading products...</div>;
    if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

    return (
        <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100 min-h-[600px]">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <ShoppingBagIcon className="w-6 h-6 mr-2 text-blue-600" />
                        Gift Shop Inventory
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Manage SightTrails merchandise and products</p>
                </div>
                <div className="flex gap-3">
                    <button
                        className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm active:scale-95"
                        onClick={() => setIsCategoryModalOpen(true)}
                    >
                        <PlusCircle className="w-5 h-5 mr-2 text-blue-600" />
                        Manage Categories
                    </button>
                    <button
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg active:scale-95"
                        onClick={openAddModal}
                    >
                        <PlusCircle className="w-5 h-5 mr-2" />
                        Add Product
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider">
                            <th className="p-4 font-semibold border-b">Product</th>
                            <th className="p-4 font-semibold border-b">Category</th>
                            <th className="p-4 font-semibold border-b">Price</th>
                            <th className="p-4 font-semibold border-b">Stock</th>
                            <th className="p-4 font-semibold border-b">Status</th>
                            <th className="p-4 font-semibold border-b text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700">
                        {(!Array.isArray(gifts) || gifts.length === 0) ? (
                            <tr>
                                <td colSpan={6} className="p-4 text-center text-gray-500 py-12">
                                    {gifts === undefined ? 'Loading products...' : 'No products found. Start by adding some merchandise.'}
                                </td>
                            </tr>
                        ) : (
                            [...gifts].sort((a, b) => {
                                const catA = a.category || 'Uncategorized';
                                const catB = b.category || 'Uncategorized';
                                
                                const indexA = customCategories.indexOf(catA);
                                const indexB = customCategories.indexOf(catB);
                                
                                if (indexA !== indexB) {
                                    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                                }
                                
                                return (a.sortOrder || 0) - (b.sortOrder || 0);
                            }).map((gift) => (
                                <tr key={gift.id} className="border-b hover:bg-gray-50 transition-colors">
                                    <td className="p-4 flex items-center gap-4">
                                        <img
                                            src={gift.imageUrl ? (gift.imageUrl.startsWith('http') ? gift.imageUrl : `${API_BASE}${gift.imageUrl.startsWith('/') ? '' : '/'}${gift.imageUrl}`) : 'https://placehold.co/150?text=No+Image'}
                                            alt={gift.name}
                                            className="w-12 h-12 object-cover rounded-xl drop-shadow-sm border border-gray-100"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = 'https://placehold.co/150?text=No+Image';
                                            }}
                                        />
                                        <div>
                                            <p className="font-semibold text-gray-900">{gift.name}</p>
                                            <p className="text-xs text-gray-500 truncate max-w-xs">{gift.description}</p>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className="px-3 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium">
                                            {gift.category || 'Uncategorized'}
                                        </span>
                                    </td>
                                    <td className="p-4 font-medium">
                                        {gift.currency} {Number(gift.price).toFixed(2)}
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${gift.stock > 10 ? 'bg-green-100 text-green-800' : gift.stock > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                            {gift.stock} in stock
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${gift.isActive ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {gift.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                title="View Details"
                                                onClick={() => setViewingGift(gift)}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                                title="Edit"
                                                onClick={() => openEditModal(gift)}
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Delete"
                                                onClick={() => handleDelete(gift.id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Category Modal */}
            {isCategoryModalOpen && (
                <GiftCategoryManager 
                    gifts={gifts} 
                    onClose={() => setIsCategoryModalOpen(false)} 
                    onGiftsUpdated={fetchGifts} 
                    showSuccess={showSuccess} 
                    showError={showError} 
                />
            )}

            {/* Product Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
                        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                            <h3 className="text-xl font-bold text-gray-800">
                                {editingGift ? 'Edit Product' : 'Add New Product'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <form 
                        onChange={(e) => {
                            if (!editingGift) {
                                const formData = new FormData(e.currentTarget);
                                const cacheObj = Object.fromEntries(formData.entries());
                                cacheObj.imageUrl = currentImageUrl;
                                localStorage.setItem('gifts_form_cache', JSON.stringify(cacheObj));
                            }
                        }}
                        onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.currentTarget);
                            const selectedCat = formData.get('category') as string;
                            
                            const blurEdges = formData.get('blurEdges') === 'on';
                            let finalImageUrl = currentImageUrl;
                            if (blurEdges && !finalImageUrl.includes('blur=true')) {
                                finalImageUrl += (finalImageUrl.includes('?') ? '&' : '?') + 'blur=true';
                            } else if (!blurEdges && finalImageUrl.includes('blur=true')) {
                                finalImageUrl = finalImageUrl.replace(/(\?|&)blur=true/, '');
                                // Clean up trailing ? if nothing else is left
                                if (finalImageUrl.endsWith('?')) finalImageUrl = finalImageUrl.slice(0, -1);
                            }
                            
                            const data = {
                                name: formData.get('name') as string,
                                description: formData.get('description') as string,
                                price: Number(formData.get('price')),
                                currency: formData.get('currency') as string,
                                stock: Number(formData.get('stock')),
                                sortOrder: Number(formData.get('sortOrder')) || 0,
                                category: selectedCat,
                                imageUrl: finalImageUrl,
                                isActive: formData.get('isActive') === 'on'
                            };
                            handleSave(data);
                        }}>
                            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Product Name</label>
                                    <input name="name" defaultValue={editingGift ? editingGift.name : cachedFormData?.name} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="e.g. Safari Graphic T-Shirt" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Description</label>
                                    <textarea name="description" defaultValue={editingGift ? editingGift.description : cachedFormData?.description} required rows={3} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="Tell us about the product..." />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Category</label>
                                    <select 
                                        name="category" 
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all bg-white"
                                        defaultValue={editingGift?.category || ''}
                                        required
                                    >
                                        <option value="" disabled>-- Select Category --</option>
                                        {Array.from(new Set(
                                            (gifts.map(g => g.category).filter(Boolean) as string[]).concat(customCategories)
                                        )).map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                                        <span>Select a category for this product.</span>
                                        <button type="button" onClick={() => setIsCategoryModalOpen(true)} className="text-blue-600 font-medium hover:underline flex items-center">
                                            Missing a category? Add New
                                        </button>
                                    </p>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-sm font-semibold text-gray-700">Price (USD)</label>
                                        <input name="price" type="number" step="0.01" defaultValue={editingGift ? editingGift.price : cachedFormData?.price} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                                        <input type="hidden" name="currency" value="USD" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-semibold text-gray-700">Stock</label>
                                        <input name="stock" type="number" defaultValue={editingGift ? editingGift.stock : cachedFormData?.stock} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-semibold text-gray-700">Sort Order</label>
                                        <input name="sortOrder" type="number" defaultValue={editingGift?.sortOrder ?? cachedFormData?.sortOrder ?? 0} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Product Image</label>
                                    <div className="flex flex-col items-center gap-4 p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-300 transition-colors bg-gray-50/50">
                                        {(previewUrl || currentImageUrl) ? (
                                            <div className="relative group">
                                                <img
                                                    src={previewUrl || (currentImageUrl.startsWith('http') ? currentImageUrl : `${API_BASE}${currentImageUrl && currentImageUrl.startsWith('/') ? '' : '/'}${currentImageUrl}`)}
                                                    alt="Preview"
                                                    className="w-32 h-32 object-cover rounded-2xl drop-shadow-md border border-white"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setCurrentImageUrl('');
                                                        if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
                                                        setPreviewUrl('');
                                                    }}
                                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-4 text-gray-400">
                                                <ImageIcon className="w-12 h-12 mb-2 opacity-20" />
                                                <p className="text-xs">No image selected</p>
                                            </div>
                                        )}

                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            className="hidden"
                                        />

                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isUploading}
                                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm text-sm font-medium"
                                        >
                                            {isUploading ? (
                                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                            ) : (
                                                <Upload className="w-4 h-4" />
                                            )}
                                            {isUploading ? 'Uploading...' : currentImageUrl ? 'Change Image' : 'Upload Image'}
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-gray-400 italic text-center">Supported formats: JPG, PNG, SVG (Max 5MB)</p>
                                </div>
                                <div className="flex items-center gap-6 pt-2">
                                    <div className="flex items-center gap-2">
                                        <input name="isActive" type="checkbox" defaultChecked={editingGift ? editingGift.isActive : true} className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer" id="isActive_check" />
                                        <label htmlFor="isActive_check" className="text-sm font-semibold text-gray-700 cursor-pointer">Published (Visible in Shop)</label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input name="blurEdges" type="checkbox" defaultChecked={editingGift ? editingGift.imageUrl.includes('blur=true') : false} className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer" id="blurEdges_check" />
                                        <label htmlFor="blurEdges_check" className="text-sm font-semibold text-gray-700 cursor-pointer">Blur Image Edges</label>
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
                                    Cancel
                                </button>
                                <button disabled={isSaving || isUploading} type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition-all active:scale-95 disabled:bg-gray-400 disabled:cursor-not-allowed">
                                    {isSaving ? 'Saving...' : isUploading ? 'Uploading...' : editingGift ? 'Update Product' : 'Create Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* View Modal */}
            {viewingGift && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                            <h3 className="text-xl font-bold text-gray-800">Product Details</h3>
                            <button onClick={() => setViewingGift(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="flex gap-6">
                                <img
                                    src={viewingGift.imageUrl ? (viewingGift.imageUrl.startsWith('http') ? viewingGift.imageUrl : `${API_BASE}${viewingGift.imageUrl.startsWith('/') ? '' : '/'}${viewingGift.imageUrl}`) : 'https://placehold.co/600x400?text=No+Image'}
                                    alt={viewingGift.name}
                                    className="w-40 h-40 object-cover rounded-3xl drop-shadow-xl border border-gray-50"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Image+Load+Error';
                                    }}
                                />
                                <div className="space-y-3">
                                    <h4 className="text-2xl font-black text-gray-900">{viewingGift.name}</h4>
                                    <div className="flex gap-2">
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-bold uppercase">{viewingGift.currency} {viewingGift.price}</span>
                                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${viewingGift.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {viewingGift.stock} Stock
                                        </span>
                                    </div>
                                    <p className="text-gray-600 text-sm leading-relaxed">{viewingGift.description}</p>
                                    <p className="text-xs text-gray-400">ID: {viewingGift.id}</p>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
                            <button onClick={() => setViewingGift(null)} className="px-6 py-2 bg-gray-900 text-white font-bold rounded-lg hover:bg-black transition-all">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Confirm Delete Modal */}
            {deletingGiftId && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all animate-fade-in-up">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Product?</h3>
                            <p className="text-gray-500 text-sm">
                                Are you sure you want to permanently delete this item from the gift shop? This action cannot be undone.
                            </p>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex gap-3">
                            <button
                                onClick={() => setDeletingGiftId(null)}
                                className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeDelete}
                                className="flex-1 px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-md transition-all active:scale-95"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GiftsAdmin;
